"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppHandler = exports.PERSISTED_SESSION_ID_KEY = void 0;
const React = require("react");
const react_1 = require("react");
const react_query_1 = require("react-query");
const useRequiredContext_1 = require("../../../../hooks/useRequiredContext");
const PlatformContext_1 = require("../../../../contexts/PlatformContext");
const ConfigContext_1 = require("../../../../contexts/ConfigContext");
const PersistenceContext_1 = require("../../../../contexts/PersistenceContext");
const useClient_1 = require("../../../../hooks/useClient");
const AppContext_1 = require("../../../../contexts/AppContext");
const errorTypes_1 = require("../../../../apis/next/client/errorTypes");
const ChaosMonkey_1 = require("../../../../apis/spg/util/ChaosMonkey");
const mockClientErrorResponse_1 = require("../../../../apis/spg/util/mockClientErrorResponse");
const useFirstRender_1 = require("../../../../hooks/useFirstRender");
// do not change this as it will not load session on apps using this handler
exports.PERSISTED_SESSION_ID_KEY = "@localStorage:savedSessionId";
/**
 * Handler resposible for rendering platform specific components and
 * handling session persistence
 */
const AppHandler = ({ children, persistedSessionIdKey = exports.PERSISTED_SESSION_ID_KEY }) => {
    // check for undefined values to give more specific error instead of using useRequiredContext
    const platformContext = (0, react_1.useContext)(PlatformContext_1.PlatformContext);
    if (Object.values(platformContext).some(x => x === undefined)) {
        throw new Error("Please wrap the AppHandler in a platform specific handler, such as AppHandlerWeb or AppHandlerRN");
    }
    const { ErrorBoundary, ErrorResolverHandler, PersistenceHandler } = platformContext.components;
    const reactQueryCacheConfig = (0, react_1.useMemo)(() => ({
        queries: {
            retry: false,
            refetchOnWindowFocus: false
        },
        mutations: {}
    }), []);
    return (React.createElement(react_query_1.ReactQueryConfigProvider, { config: reactQueryCacheConfig },
        React.createElement(ErrorBoundary, null,
            React.createElement(ErrorResolverHandler, null,
                React.createElement(PersistenceHandler, { provider: window.localStorage },
                    React.createElement(AppHandlerInner, { persistedSessionIdKey: persistedSessionIdKey }, children))))));
};
exports.AppHandler = AppHandler;
const AppHandlerInner = ({ children, persistedSessionIdKey }) => {
    const { config } = (0, useRequiredContext_1.useRequiredContext)(ConfigContext_1.ConfigContext);
    const [persistSession, setPersistSession] = (0, react_1.useState)(config.persistSessionOnLoad);
    const [sessionPersistenceComplete, setSessionPersistenceComplete] = (0, react_1.useState)(false);
    const [storageSessionId, setStorageSessionId] = (0, react_1.useState)(null);
    const persistenceContext = (0, useRequiredContext_1.useRequiredContext)(PersistenceContext_1.PersistenceContext);
    const client = (0, useClient_1.useClient)();
    // calling 'mockRequestErrors', 'mockClientErrorResponse' and 'chaosMonkey'
    // methods was previously done inside a useEffect hook. however, as this does
    // not run until after the first render, some handlers/components would be
    // able to use client methods as usual for the first render, after which they
    // would then fail.
    // to fix this, we use 'useFirstRender' to ensure that any client methods
    // that should fail do so from the first render.
    (0, useFirstRender_1.useFirstRender)(() => {
        if (config.debugRequestErrorCount) {
            (0, mockClientErrorResponse_1.mockRequestErrors)(client, config.debugRequestErrorCount, config.useRealClient, config.debugErrorApproach);
        }
        if (config.debugClientMethodsError) {
            (0, mockClientErrorResponse_1.mockClientErrorResponse)(client, config.debugClientMethodsError, null, config.debugErrorApproach);
        }
        if (config.debugChaosProbability) {
            (0, ChaosMonkey_1.chaosMonkey)(client, config.debugChaosProbability, config.useRealClient);
        }
    });
    const { refetch: sessionPersistenceRetry, error: sessionPersistenceError, isLoading: sessionPersistenceLoading, clear } = (0, react_query_1.useQuery)(["sessionRestore", storageSessionId], () => client.auth.setSessionFromSessionId(storageSessionId), {
        enabled: false,
        retry: false,
        onSuccess: () => setSessionPersistenceComplete(true)
    });
    // runs when persiting session (based on config.persistSessionOnLoad)
    (0, react_1.useEffect)(() => {
        if (persistSession === false)
            return;
        const handleSessionChange = async () => {
            // handle persistence of session id
            const session = client.auth.session;
            if (session === null) {
                await persistenceContext.removeItem(persistedSessionIdKey);
            }
            else {
                await persistenceContext.setItem(persistedSessionIdKey, session.sessionId);
            }
        };
        // this is needed in case the client.auth.logout fails as it will not trigger a sessionChange
        const handleBeforeLogout = async () => {
            // remove persisted sessionId
            await persistenceContext.removeItem(persistedSessionIdKey);
            // manually reset useQuery state
            clear();
            // set persistence complete to true as we just logged out, so there will not be a sessionId to persist
            // this avoids blocking UI responding to sessionPersitenceComplete when false
            setSessionPersistenceComplete(true);
        };
        client.auth.on("sessionChange", handleSessionChange);
        client.auth.on("beforeLogout", handleBeforeLogout);
        // load session if any persisted
        persistenceContext.getItem(persistedSessionIdKey).then(sessionId => {
            // set local storage sessionId in a state flag,
            // picked up by useEffect to call client.auth.setSessionFromSessionId via useQuery
            setStorageSessionId(sessionId || null);
            if (!sessionId) {
                // if no session to persist set completion flag
                setSessionPersistenceComplete(true);
                return;
            }
        });
        return () => {
            client.auth.off("sessionChange", handleSessionChange);
            client.auth.off("beforeLogout", handleBeforeLogout);
        };
    }, [client.auth, persistSession, persistedSessionIdKey]);
    // try and restore session when we have session id in local storage
    (0, react_1.useEffect)(() => {
        if (!storageSessionId) {
            return;
        }
        sessionPersistenceRetry();
    }, [storageSessionId, sessionPersistenceRetry]);
    // when error restoring session, check type
    (0, react_1.useEffect)(() => {
        if (!sessionPersistenceError)
            return;
        if (sessionPersistenceError instanceof errorTypes_1.NextSessionInvalidatedError) {
            // logout to kill session. clean up picked up by beforeLogout listener above
            client.auth.logout().catch(console.error);
        }
    }, [sessionPersistenceError]);
    // for manual override in case config.persistSessionOnLoad is false
    const handlePersistSession = (0, react_1.useCallback)(() => {
        setPersistSession(true);
    }, []);
    const appContextValue = (0, react_1.useMemo)(() => ({
        persistSession: handlePersistSession,
        sessionPersistenceComplete,
        sessionPersistenceLoading,
        sessionPersistenceError,
        sessionPersistenceRetry
    }), [
        handlePersistSession,
        sessionPersistenceComplete,
        sessionPersistenceLoading,
        sessionPersistenceError,
        sessionPersistenceRetry
    ]);
    return (React.createElement(AppContext_1.AppContext.Provider, { value: appContextValue }, children));
};
//# sourceMappingURL=index.js.map